import { useState } from "react";
import PageLayout, { Section } from "./PageLayout";
import { Search, ChevronDown, MessageCircle, Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";

const FAQS = [
  { cat: "Account", q: "How do I create an account?", a: "Click 'Start Watching' on the homepage, enter your name, email and a password. You'll be able to start streaming immediately and add a payment method later." },
  { cat: "Account", q: "I forgot my password — how do I reset it?", a: "Go to the sign-in page and click 'Forgot password?'. Enter your email and we'll send a reset link valid for 1 hour." },
  { cat: "Account", q: "How do I add or remove profiles?", a: "After signing in, you'll land on the 'Who's watching?' screen. Click 'Manage Profiles' to add up to 5 profiles or remove existing ones. Kids profiles are limited to family-safe content." },
  { cat: "Billing", q: "What payment methods are accepted?", a: "Djino Stream currently supports PayPal. We're rolling out direct credit/debit card support and mobile money in 2026." },
  { cat: "Billing", q: "Can I cancel anytime?", a: "Yes. Visit Subscriptions → Cancel. You'll keep access until the end of your current billing period. No questions asked." },
  { cat: "Billing", q: "How does the 3-day free trial work?", a: "New accounts can start a 3-day free trial. PayPal authorization is required to verify your card, but you will not be charged during the trial. Cancel before day 3 to avoid being charged." },
  { cat: "Streaming", q: "What devices are supported?", a: "Browsers (Chrome, Safari, Firefox, Edge), iOS, Android, smart TVs, and Chromecast/AirPlay. See our Devices page for the full list." },
  { cat: "Streaming", q: "Why is my video buffering?", a: "Try lowering quality in the player Settings, or check your connection (5 Mbps recommended for HD, 25 Mbps for 4K). Restarting the app usually helps." },
  { cat: "Streaming", q: "Can I download titles to watch offline?", a: "Yes, on Standard and Premium plans. Open any movie or episode and tap the download icon next to the play button." },
  { cat: "Live TV", q: "Are live channels included in my plan?", a: "Yes. All plans include access to our 12+ live TV channels — news, sports, music, kids and more." },
];

export default function HelpCenter() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const filtered = q ? FAQS.filter((f) => (f.q + f.a + f.cat).toLowerCase().includes(q.toLowerCase())) : FAQS;
  const cats = Array.from(new Set(filtered.map((f) => f.cat)));

  return (
    <PageLayout
      eyebrow="Help Center"
      title="How can we help?"
      subtitle="Search common questions or jump into a topic below. Our team replies within 24 hours."
    >
      <div className="relative mb-10">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#888]" size={20} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search help..."
          className="w-full pl-12 pr-4 py-4 bg-[#0d0d0d] border border-white/10 rounded-xl text-white text-lg focus:border-[#E50914] focus:outline-none"
          data-testid="help-search"
        />
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/contact" className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-[#E50914]/40 transition flex items-center gap-4" data-testid="help-contact-card">
          <div className="w-11 h-11 rounded-lg bg-[#E50914]/15 text-[#E50914] flex items-center justify-center"><MessageCircle size={20} /></div>
          <div><div className="font-bold" style={{ fontFamily: "Outfit" }}>Contact us</div><div className="text-xs text-[#888]">Talk to a real human</div></div>
        </Link>
        <a href="mailto:support@djinostream.com" className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-[#E50914]/40 transition flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#E50914]/15 text-[#E50914] flex items-center justify-center"><Mail size={20} /></div>
          <div><div className="font-bold" style={{ fontFamily: "Outfit" }}>Email support</div><div className="text-xs text-[#888]">support@djinostream.com</div></div>
        </a>
        <Link to="/devices" className="bg-[#0d0d0d] border border-white/5 rounded-xl p-5 hover:border-[#E50914]/40 transition flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#E50914]/15 text-[#E50914] flex items-center justify-center"><Phone size={20} /></div>
          <div><div className="font-bold" style={{ fontFamily: "Outfit" }}>Supported devices</div><div className="text-xs text-[#888]">Where you can watch</div></div>
        </Link>
      </div>

      {cats.map((cat) => (
        <Section key={cat} title={cat}>
          <div className="space-y-2">
            {filtered.filter((f) => f.cat === cat).map((f, i) => {
              const id = `${cat}-${i}`;
              const isOpen = open === id;
              return (
                <div key={id} className="bg-[#0d0d0d] border border-white/5 rounded-xl overflow-hidden" data-testid={`faq-${id}`}>
                  <button onClick={() => setOpen(isOpen ? null : id)} className="w-full text-left p-5 flex items-center justify-between hover:bg-[#141414] transition">
                    <span className="font-semibold">{f.q}</span>
                    <ChevronDown className={`text-[#888] transition ${isOpen ? "rotate-180 text-[#E50914]" : ""}`} size={18} />
                  </button>
                  {isOpen && <div className="px-5 pb-5 text-[#bbb] text-sm leading-relaxed">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </Section>
      ))}

      {filtered.length === 0 && <div className="text-center text-[#888] py-12">No matches. Try a different search or <Link to="/contact" className="text-[#E50914]">contact us</Link>.</div>}
    </PageLayout>
  );
}
